import { Link, useNavigate, useLocation } from "react-router-dom";
import { Users, ChartLineUp, SignOut } from "@phosphor-icons/react";
import { useAdmin, logout as endSession } from "@/lib/auth";

// Xero Connections is now merged into the Client Directory (per-card status
// chip + refresh action), and the Report Templates section was retired in
// favour of the per-client WYSIWYG report config. Firm Settings was retired
// per user request (Feb 2026) — configuration lives per-client. This nav is
// deliberately short as a result.
const NAV = [
  { to: "/admin", label: "Client Directory", Icon: Users, testid: "nav-clients" },
];

export default function AppShell({ children, title, subtitle, actions }) {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAdmin() || {};
  const logout = async (everywhere = false) => {
    await endSession(everywhere);
    navigate("/login", { replace: true });
  };

  const isActive = (to) =>
    to === "/admin" ? location.pathname === to : location.pathname.startsWith(to);

  return (
    <div className="min-h-screen bg-[#F5F5F5] flex">
      {/* Sidebar */}
      <aside className="w-[240px] shrink-0 bg-[#1C1917] text-white flex flex-col h-screen sticky top-0">
        <div className="px-6 py-6 border-b border-white/10">
          <Link to="/admin" className="flex items-center gap-2.5">
            <ChartLineUp size={20} weight="duotone" />
            <div className="leading-tight">
              <div className="font-display text-lg tracking-tight">FLASH</div>
              <div className="text-[9px] uppercase tracking-[0.18em] text-white/50 font-mono">Prysm Financial</div>
            </div>
          </Link>
        </div>
        <nav className="flex-1 py-4 px-3 space-y-0.5">
          {NAV.map((n) => {
            const active = isActive(n.to);
            return (
              <Link
                key={n.to}
                to={n.to}
                data-testid={n.testid}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${
                  active ? "bg-white/10 text-white" : "text-white/65 hover:bg-white/5 hover:text-white"
                }`}
              >
                <n.Icon size={16} weight={active ? "fill" : "duotone"} />
                <span>{n.label}</span>
              </Link>
            );
          })}
        </nav>
        <div className="px-4 py-4 border-t border-white/10 text-xs">
          <div className="text-white/60 font-mono truncate mb-2">{user.email}</div>
          <button onClick={() => logout(false)} data-testid="logout-btn" className="flex items-center gap-1.5 text-white/60 hover:text-white">
            <SignOut size={14} /> Sign out
          </button>
          <button onClick={() => logout(true)} data-testid="logout-all-btn" className="mt-1.5 text-[11px] text-white/40 hover:text-white underline-offset-2 hover:underline">
            Sign out everywhere
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0 px-8 lg:px-12 py-10">
        {(title || actions) && (
          <div className="flex items-end justify-between mb-10 gap-6 flex-wrap">
            <div>
              {subtitle && <p className="uppercase text-xs tracking-[0.2em] text-[#57534E] mb-3 font-medium">{subtitle}</p>}
              {title && <h1 className="font-display text-4xl lg:text-5xl tracking-tight leading-none">{title}</h1>}
            </div>
            {actions}
          </div>
        )}
        {children}
      </main>
    </div>
  );
}
